import { useState } from 'react';
import cn from 'classnames';
import { Autocomplete, AutocompleteItem } from '@heroui/react';
import { formatDuration, getDurationOptions } from '../../../config/durationOptions';
import styles from './DurationField.module.scss';

const MAX_DURATION_MINUTES = 24 * 60;

/** "50" → 50, "1h 20", "1h 20 min" → 80, "2h" → 120; anything else → null */
const parseDuration = (text: string) => {
  const value = text.trim().toLowerCase();
  if (!value) return 0;
  if (/^\d+$/.test(value)) return Number(value);
  const match = value.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*(?:m|min)?)?$/);
  if (!match || (!match[1] && !match[2])) return null;
  return Number(match[1] ?? 0) * 60 + Number(match[2] ?? 0);
};

interface DurationFieldProps {
  value: number;
  onChange: (minutes: number) => void;
  onBlur?: () => void;
  errorMessage?: string;
  isRequired?: boolean;
}

/** Duration in minutes: one of the suggested values or any number typed in. */
export const DurationField = ({
  value,
  onChange,
  onBlur,
  errorMessage,
  isRequired,
}: DurationFieldProps) => {
  // Text being typed; null shows the saved value
  const [draft, setDraft] = useState<string | null>(null);
  const options = getDurationOptions(value);
  // Selecting an option also reports its label as typed text, which must not filter the list
  const query = draft !== null && draft !== formatDuration(value) ? draft.trim() : '';
  const items = query
    ? options.filter((option) => option.label.includes(query) || option.key.startsWith(query))
    : options;

  const commit = () => {
    if (draft !== null) {
      const minutes = parseDuration(draft);
      onChange(minutes !== null && minutes <= MAX_DURATION_MINUTES ? minutes : 0);
      setDraft(null);
    }
    onBlur?.();
  };

  return (
    <div className="w-full">
      <Autocomplete
        label="Duration"
        labelPlacement="outside"
        placeholder="Enter duration"
        isRequired={isRequired}
        allowsCustomValue
        items={items}
        inputValue={draft ?? (value ? formatDuration(value) : '')}
        selectedKey={draft === null && value ? String(value) : null}
        onInputChange={setDraft}
        onSelectionChange={(key) => {
          if (key === null) return;
          onChange(Number(key));
          setDraft(null);
        }}
        onBlur={commit}
        popoverProps={{ shouldBlockScroll: false, classNames: { content: styles.popoverContent } }}
        listboxProps={{ className: styles.listbox }}
        inputProps={{
          classNames: {
            label: styles.label,
            inputWrapper: cn(styles.inputWrapper, { [styles.invalid]: !!errorMessage }),
          },
        }}
      >
        {(item) => <AutocompleteItem key={item.key}>{item.label}</AutocompleteItem>}
      </Autocomplete>
      {errorMessage && (
        <p className={cn('p-1 text-tiny', styles.errorMessage)} role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
};
